/* eslint-disable @typescript-eslint/no-magic-numbers */
import { ReactElement, useRef, useState } from 'react';
import { BsThreeDotsVertical } from 'react-icons/bs';

import useClickAway from '@/hooks/useClickAway';

interface MenuOption {
  onClick: () => void;
  label: string;
  icon: ReactElement;
  update?: boolean;
}

interface MenuProperties {
  menuOptions: MenuOption[];
  outline?: boolean;
  buttonStyles?: string;
  showNotificationDot?: boolean;
}

export default function Menu({
  menuOptions,
  buttonStyles,
  outline,
  showNotificationDot,
}: MenuProperties) {
  const [open, setOpen] = useState(false);
  const reference = useRef(null);

  const onMenuClick = () => {
    setOpen((previous) => !previous);
  };

  useClickAway(reference, () => {
    setOpen(false);
  });

  const defaultStyles =
    'z-elevated flex h-[40px] w-[40px] items-center justify-center rounded-full bg-neutral-white';

  const outlineStyles = outline ? ' border-[3px] border-primary-blue-3' : '';
  return (
    <div className="relative flex flex-col items-end justify-end" ref={reference}>
      <button
        type="button"
        className={`${defaultStyles} ${buttonStyles} ${outlineStyles}`}
        onClick={onMenuClick}
        aria-label="Toggle menu"
      >
        <BsThreeDotsVertical
          style={{ fontSize: outline ? '1.5625em' : '1.6em', color: '#002F76' }}
        />
        {showNotificationDot && (
          <span className="absolute right-0 top-0 h-3 w-3 rounded-full border-2 border-white bg-red-500" />
        )}
      </button>
      {open && (
        <>
          <div
            className={`absolute  ${
              outline ? 'right-4 top-12' : 'right-6 top-16'
            } z-modal flex  flex-col items-center rounded-[9px] bg-neutral-white shadow-custom-2`}
          >
            {menuOptions.map((option, index) => {
              return (
                <>
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => {
                      option.onClick();
                      setOpen(false);
                    }}
                    className="flex h-[56px] w-[273px] items-center rounded-[9px] px-[22px] hover:bg-neutral-grey-1 hover:bg-opacity-50"
                  >
                    {option.icon}
                    <span className="ml-2 font-medium text-neutral-grey-3">{option.label}</span>
                    {option.update && (
                      <img
                        src="./icons/error-icon.svg"
                        alt="error icon"
                        className="absolute right-2 h-7 w-7"
                      />
                    )}
                  </button>
                  {index < menuOptions.length - 1 && <hr className="w-[80%]" />}
                </>
              );
            })}
          </div>
          <div
            className={`absolute right-4 top-10  ${
              outline ? 'right-4 top-10' : 'right-6 top-14'
            } h-0 w-0 border-b-[10px] border-r-[15px] border-t-[10px] border-b-transparent border-r-neutral-white border-t-transparent`}
          />
        </>
      )}
    </div>
  );
}

Menu.defaultProps = {
  outline: false,
  buttonStyles: '',
  showNotificationDot: false,
};
